<script>
    import * as m from '@app/paraglide/messages.js';
    import { onMount } from 'svelte';
    import { projectState } from "@app/store/projectStore.svelte.js";
    import { eventGroupState, isSelectedEventGroupLocked } from "@app/store/eventGroupStore.svelte.js";
    import { toast } from "@zerodevx/svelte-toast";
    import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
    import { lockState } from "@app/store/lockStore.svelte.js";
    import escapeRegExp from "@cclib/functions/quill/escapeRegExp.js";
    import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";

    let searchValue = $state(),
        replaceValue = $state(),
        caseSensitive = $state(false),
        replaceAllOption = $state(false),
        regexOption = $state(false),
        wholeWord = $state(false),
        results = $state([]),
        selectedResult = $state();

    let isLocked = $derived(isSelectedEventGroupLocked());

    onMount(() => {
        // console.log("EFFECT Focusing on input");
        //Focus on input with id of FindInput
        setTimeout(() => {
            document.getElementById("FindInput").focus();
        }, 100);
    });

    function buildSearchPattern() {
        const flags = caseSensitive ? "gm" : "gmi";
        const source = regexOption ? searchValue : escapeRegExp(searchValue);
        if (wholeWord) {
            return new RegExp(`(?<![\\p{L}\\p{N}_])${source}(?![\\p{L}\\p{N}_])`, flags + "u");
        }
        return new RegExp(source, flags);
    }

    function clearResults() {
        results = [];
        selectedResult = undefined;
    }

    function search(ev) {
        ev.preventDefault();

        //console.log("Before:", searchValue, replaceValue);
        searchValue = document.getElementById("FindInput").value;
        replaceValue = document.getElementById("ReplaceInput").value;
        //console.log("After:", searchValue, replaceValue);

        let searchPattern = buildSearchPattern();

        results = eventGroupState[projectState.selected].events.filter(
            (event) => {
                return convertToPlainText(event.text, " ").match(searchPattern);
            },
        );

        toast.push(m.find_replace_search_results_toast({count: results.length}), {
            classes: ["toast-info"],
        });

        try {
            if (results.length > 0) {
                selectedResult = 0;
                selectResult();
            } else if (window.quillEditor) {
                window.quillEditor.blur();
            }
        } catch (err) {
            console.log(err.message);
        }
    }

    function replaceAll() {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        replaceValue = document.getElementById("ReplaceInput").value;
        let searchPattern = buildSearchPattern();

        replaceValue = !replaceValue ? "" : replaceValue;

        for (
            let i = 0;
            i < eventGroupState[projectState.selected].events.length;
            i++
        ) {
            eventGroupState[projectState.selected].events[i].text =
                eventGroupState[projectState.selected].events[i].text.replace(
                    searchPattern,
                    replaceValue,
                );
        }

        toast.push(m.find_replace_replace_all_toast(), {
            classes: ["toast-info"],
        });        
        results = [];
        selectedResult = undefined;
        eventGroupState[projectState.selected].selected = [];

        insertHistoryAction({
            name: "replace text", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    }

    function replaceOnce() {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        replaceValue = document.getElementById("ReplaceInput").value;
        let searchPattern = buildSearchPattern();

        replaceValue = !replaceValue ? "" : replaceValue;

        let eventIndex = eventGroupState[
            projectState.selected
        ].events.findIndex((event) => {
            return event.id === results[selectedResult].id;
        });

        eventGroupState[projectState.selected].events[eventIndex].text =
            eventGroupState[projectState.selected].events[
                eventIndex
            ].text.replace(searchPattern, replaceValue);

        results = results.filter((result) => {
            return result.id !== results[selectedResult].id;
        });

        toast.push(m.find_replace_replace_once_toast(), {
            classes: ["toast-info"],
        });

        if (results.length > 0 && results[selectedResult]) {
            selectResult();
        } else {
            nextResult();
        }

        insertHistoryAction({
            name: "replace text", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    }

    function prevResult() {
        selectedResult =
            selectedResult > 0 ? --selectedResult : results.length - 1;
        selectResult();
    }

    function nextResult() {
        if (results.length > 0) {
            selectedResult =
                selectedResult + 1 < results.length ? ++selectedResult : 0;
            selectResult();
        } else {
            selectedResult = undefined;
            eventGroupState[projectState.selected].selected = [];
        }
    }    
    
    async function selectResult() {
        if (!isNaN(selectedResult) && results.length > 0) {
            /* Scroll and select the event */
            let eventIndex = eventGroupState[
                projectState.selected
            ].events.findIndex((event) => {
                return event.id === results[selectedResult].id;
            });

            eventGroupState[projectState.selected].selected = [eventIndex];

            const selectedIndex = eventGroupState[projectState.selected].selected[0];
            
            // Dispatch custom event to scroll to the selected event
            window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
                detail: { index: selectedIndex }
            }));

            if (lockState.video && player){
                player.currentTime = eventGroupState[projectState.selected].events[selectedIndex].start;
            }

            /* Highlight the search value */
            let searchPattern = buildSearchPattern();

            let text = convertToPlainText(results[selectedResult].text, " ");
            let regexResult = searchPattern.exec(text);
            
            setTimeout(function () {
                try {
                    window.quillEditor.setSelection(
                        regexResult.index,
                        regexResult[0].length,
                    );
                } catch (err) {
                    console.log(err, err.message, regexResult);
                }
            }, 100);
        }
    }
</script>
 
<form id="SearchAndReplaceForm" onsubmit={(e) => search(e)} aria-label={m.find_replace_form_aria_label()}>
    <div class="row mb-3">
        <div class="col">
            <label class="form-label" for="FindInput">{m.find_replace_search_label()}</label>
            <div class="input-group input-group-sm">
                <input
                    id="FindInput"
                    class="form-control"
                    type="text"
                    bind:value={searchValue}
                    oninput={clearResults}
                />
            </div>
        </div>
        <div class="col">
            <label class="form-label" for="ReplaceInput">{m.find_replace_replace_label()}</label>
            <div class="input-group input-group-sm">
                <input
                    id="ReplaceInput"
                    class="form-control"
                    type="text"
                    bind:value={replaceValue}
                />
            </div>
        </div>
    </div>
    <div class="row mb-3">
        <div class="col">
            <div class="form-check">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="caseSensitive"
                    bind:checked={caseSensitive}
                    onchange={clearResults}
                />
                <label class="form-check-label" for="caseSensitive"
                    >{m.find_replace_case_sensitive_label()}</label
                >
            </div>
        </div>
        <div class="col">
            <div class="form-check">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="wholeWordCheck"
                    bind:checked={wholeWord}
                    onchange={clearResults}
                />
                <label class="form-check-label" for="wholeWordCheck"
                    >{m.find_replace_whole_word_label()}</label
                >
            </div>
        </div>
        <div class="col">
            <div class="form-check">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="replaceAllCheck"
                    bind:checked={replaceAllOption}
                />
                <label class="form-check-label" for="replaceAllCheck"
                    >{m.find_replace_replace_all_label()}</label
                >
            </div>
        </div>
        <div class="col">
            <div class="form-check">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="regexCheck"
                    bind:checked={regexOption}
                    onchange={clearResults}
                />
                <label class="form-check-label" for="regexCheck">{m.find_replace_regex_label()}</label>
            </div>
        </div>
    </div>
    <div class="btn-group mt-2" role="group">
        <button class="btn btn-primary" type="submit" onclick={(e) => search(e)} id="searchButton" aria-label={m.find_replace_search_button_aria_label()}>
            <i class="bi bi-search"></i> {m.find_replace_search_button()}
        </button>
        <button
            class="btn btn-primary"
            type="button"
            disabled={results.length === 0 || isLocked}
            onclick={() => (replaceAllOption ? replaceAll() : replaceOnce())}
            id="replaceButton"
            aria-label={m.find_replace_replace_button_aria_label()}
        >
            <i class="bi bi-arrow-left-right"></i> {m.find_replace_replace_button()}
        </button>
        <button
            class="btn btn-light"
            type="button"
            disabled={results.length === 0}
            onclick={prevResult}
            id="prevResultButton"
            aria-label={m.find_replace_prev_result_aria_label()}
        >
            <i class="bi bi-chevron-left"></i>
        </button>
        <button
            class="btn btn-light"
            type="button"
            disabled={results.length === 0}
            onclick={nextResult}
            id="nextResultButton"
            aria-label={m.find_replace_next_result_aria_label()}
        >
            <i class="bi bi-chevron-right"></i>
        </button>
        {#if selectedResult !== undefined}
            <button class="btn btn-light text-dark" type="button" disabled="">
                {selectedResult + 1}/{results.length}
            </button>
        {/if}
    </div>
    {#if results.length === 0}
        <small class="form-text align-middle text-danger m-2">{m.find_replace_no_results()}</small>
    {/if}
</form>
